import type { RouteRecordRaw } from 'vue-router'
import { area, deviceType, page, layout } from '@/router/routes/utils'
import { areas } from '@/router/areas'
import { DeviceType } from '@/api/client'
import { useProjectStore } from '@/stores/ProjectStore'
import { useAuthStore } from '@/stores/AuthStore'
import StoreManager from '@/classes/manager/StoreManager'
import { getUserStoresToSync } from '@/stores'
import { useEmployeeStore } from '@/stores/EmployeeStore'
import { BarBackgroundStyle } from '@/composables/useNativeStyle'
import PersonalAppManagerOnboardingPage from '@/pages/new-onboarding/PersonalAppManagerOnboardingPage.vue'
import MyAreaOnboardingPage from '@/pages/new-onboarding/MyAreaOnboardingPage.vue'

const newOnboarding: Array<RouteRecordRaw> = deviceType(
  [DeviceType.PERSONAL],
  [
    {
      path: '/new-onboarding/personal-app-manager',
      name: 'personalAppManagerNewOnboarding',
      component: PersonalAppManagerOnboardingPage,
      meta: {
        statusBarBackgroundStyle: BarBackgroundStyle.WHITE,
        navigationBarBackgroundStyle: BarBackgroundStyle.WHITE,
      },
    },
    {
      path: '/new-onboarding/my-area',
      name: 'myAreaNewOnboarding',
      component: MyAreaOnboardingPage,
      meta: {
        statusBarBackgroundStyle: BarBackgroundStyle.WHITE,
        navigationBarBackgroundStyle: BarBackgroundStyle.WHITE,
      },
    },
  ],
)

const singleUserRoutesForMyArea: Array<RouteRecordRaw> = deviceType(
  [DeviceType.PERSONAL, DeviceType.TEAM],

  area(areas.MY_AREA, [
    {
      path: '/myArea',
      name: 'myArea',
      meta: {
        isStateful: true,
        showPill: false,
        scrollable: true,
        headerTitle: 'phrases.my_area',
        showBackButton: false, //dynamically turned to true when Teamdevice in beforeEnter Callback
        disableNativeBackButton: true,
        statusBarBackgroundStyle: BarBackgroundStyle.WHITE,
        navigationBarBackgroundStyle: BarBackgroundStyle.WHITE,
      },
      beforeEnter: (to, _from, next) => {
        const employeeStore = useEmployeeStore()
        const employee = employeeStore.employee
        if (!employee) return
        //no await to prevent blocking
        void employeeStore.refreshEmployee(employee)
        // employee.can_visit_my_area could be not updated yet
        if (useAuthStore().isPersonalDevice && employee.can_visit_my_area) {
          void StoreManager.sync(getUserStoresToSync(employee))
        }
        //-> we can not set the boolean in the meta part, because then we would read the value
        // before authstore is initialized which crashes the app
        to.meta.showBackButton = useAuthStore().isTeamDevice
        next()
      },
      component: page('my-area/single-user/MyAreaPage'),
    },
    {
      path: '/correction',
      name: 'correction',
      meta: {
        isStateful: true,
        showHeader: false,
        scrollable: true,
        statusBarBackgroundStyle: BarBackgroundStyle.WHITE,
        navigationBarBackgroundStyle: BarBackgroundStyle.WHITE,
      },
      component: page('correction/single-user/CorrectionPage'),
    },
    {
      path: '/absences/:aiGeneratedAbsenceUuid?',
      name: 'absences',
      meta: {
        isStateful: true,
        showHeader: false,
        scrollable: true,
        statusBarBackgroundStyle: BarBackgroundStyle.WHITE,
        navigationBarBackgroundStyle: BarBackgroundStyle.WHITE,
      },
      props: (route) => {
        return {
          aiGeneratedAbsenceUuid: route.params.aiGeneratedAbsenceUuid,
        }
      },
      component: page('absence/single-user/AbsencesPage'),
    },
    {
      path: '/absence/:id',
      name: 'absence',
      meta: {
        isStateful: true,
        showHeader: false,
        scrollable: false,
        statusBarBackgroundStyle: BarBackgroundStyle.WHITE,
        navigationBarBackgroundStyle: BarBackgroundStyle.WHITE,
      },
      props: (route) => ({ id: Number(route.params.id) }),
      component: page('absence/single-user/AbsencePage'),
    },
    {
      path: '/shiftPlan',
      name: 'shiftPlan',
      meta: {
        isStateful: true,
        showHeader: false,
        scrollable: true,
        statusBarBackgroundStyle: BarBackgroundStyle.WHITE,
        navigationBarBackgroundStyle: BarBackgroundStyle.WHITE,
      },
      component: page('shift-plan/single-user/ShiftPlanPage'),
    },
    {
      path: '/shifts',
      name: 'shifts',
      meta: {
        isStateful: true,
        showHeader: false,
        scrollable: true,
        statusBarBackgroundStyle: BarBackgroundStyle.WHITE,
        navigationBarBackgroundStyle: BarBackgroundStyle.WHITE,
      },
      props: (route) => ({ id: String(route.params.id) }),
      component: page('shift-plan/single-user/ShiftsPage'),
    },
    {
      path: '/shift/:id',
      name: 'shift',
      meta: {
        isStateful: true,
        showHeader: false,
        scrollable: true,
        statusBarBackgroundStyle: BarBackgroundStyle.WHITE,
        navigationBarBackgroundStyle: BarBackgroundStyle.WHITE,
      },
      props: (route) => ({ id: Number(route.params.id) }),
      component: page('shift-plan/single-user/ShiftPage'),
    },
    {
      path: '/calendar',
      name: 'calendar',
      meta: {
        isStateful: true,
        showHeader: false,
        scrollable: true,
        statusBarBackgroundStyle: BarBackgroundStyle.WHITE,
        navigationBarBackgroundStyle: BarBackgroundStyle.WHITE,
      },
      props: (route) => ({
        name: route.query.name ? String(route.query.name) : undefined,
      }),
      component: page('shift-plan/single-user/CalendarPage'),
    },
  ]),
)

const singleUserRoutesForWorkArea: Array<RouteRecordRaw> = deviceType(
  [DeviceType.PERSONAL, DeviceType.TEAM],
  area(areas.WORK_AREA, [
    {
      path: '/home',
      name: 'home',
      meta: {
        showGradient: true,
        showTime: false,
        scrollable: false,
        disableNativeBackButton: true,
        statusBarBackgroundStyle: BarBackgroundStyle.PRIMARY,
        navigationBarBackgroundStyle: BarBackgroundStyle.WHITE,
      },
      component: page('single-user/HomePage'),
      beforeEnter: (to) => {
        if (useAuthStore().isTeamDevice) {
          to.meta.scrollable = true
        }
      },
    },
    {
      path: '/task/:id/:projectId?',
      name: 'task',
      meta: {
        scrollable: false,
        disableNativeBackButton: true,
        statusBarBackgroundStyle: BarBackgroundStyle.WHITE,
        navigationBarBackgroundStyle: BarBackgroundStyle.WHITE,
      },
      props: (route) => ({
        id: Number(route.params.id),
        projectId: Number(route.params.projectId),
      }),
      component: page('single-user/TaskPage'),
      beforeEnter: (to) => {
        if (to.params.projectId) {
          to.meta.scrollable = true
        }
      },
    },
    {
      path: '/projects',
      name: 'projects',
      meta: {
        alwaysUnmount: true,
        showHeader: false,
        scrollable: true,
        disableNativeBackButton: true,
        statusBarBackgroundStyle: BarBackgroundStyle.WHITE,
        navigationBarBackgroundStyle: BarBackgroundStyle.WHITE,
      },
      beforeEnter: async () => {
        await useProjectStore().loadProjects()
      },
      component: page('single-user/projects/projects-page/ProjectsPage'),
    },
    {
      path: '/project/:id',
      name: 'project',
      meta: {
        scrollable: true,
        statusBarBackgroundStyle: BarBackgroundStyle.WHITE,
        navigationBarBackgroundStyle: BarBackgroundStyle.WHITE,
      },
      beforeEnter: async (to) => {
        try {
          await useProjectStore().setActiveProjectById(Number(to.params.id))
        } catch (e) {
          console.error(e)
        }
      },
      component: page('single-user/projects/project-page/ProjectPage'),
    },
    {
      path: '/project/:id/subprojects',
      name: 'subprojects',
      meta: {
        statusBarBackgroundStyle: BarBackgroundStyle.WHITE,
        navigationBarBackgroundStyle: BarBackgroundStyle.WHITE,
      },
      beforeEnter: async () => {
        await useProjectStore().loadSubsOfMainAndActive()
      },
      component: page('single-user/projects/sub-projects-page/SubProjectsPage'),
    },
    {
      path: '/project/:projectId/checklists',
      name: 'checklists',
      meta: {
        scrollable: true,
        statusBarBackgroundStyle: BarBackgroundStyle.WHITE,
        navigationBarBackgroundStyle: BarBackgroundStyle.WHITE,
      },
      props: (route) => ({ projectId: Number(route.params.projectId) }),
      component: page('single-user/projects/checklist/ChecklistsPage'),
    },
    {
      path: '/project/:id/documentation/:aiGeneratedDocumentationText?',
      name: 'projectDocumentation',
      meta: {
        isStateful: true,
        scrollable: true,
        statusBarBackgroundStyle: BarBackgroundStyle.WHITE,
        navigationBarBackgroundStyle: BarBackgroundStyle.WHITE,
      },
      props: (route) => ({
        projectId: Number(route.params.id),
        aiGeneratedDocumentationText: route.params.aiGeneratedDocumentationText,
      }),
      component: page('single-user/projects/DocumentationPage'),
    },
    {
      path: '/project/:projectId/confirmation',
      name: 'projectConfirmation',
      meta: {
        isStateful: true,
        statusBarBackgroundStyle: BarBackgroundStyle.WHITE,
        navigationBarBackgroundStyle: BarBackgroundStyle.WHITE,
      },
      props: (route) => route.meta.props,
      component: page(
        'single-user/projects/confirmation-page/ConfirmationPage',
      ),
    },
    {
      path: '/project/:projectId/video-documentation',
      name: 'videoDocumentation',
      meta: {
        isStateful: true,
        statusBarBackgroundStyle: BarBackgroundStyle.WHITE,
        navigationBarBackgroundStyle: BarBackgroundStyle.WHITE,
      },
      props: (route) => route.meta.props,
      component: page('single-user/projects/video-page/VideoPage'),
    },
    {
      path: '/project/contacts',
      name: 'contacts',
      meta: {
        isStateful: false,
        scrollable: true,
        statusBarBackgroundStyle: BarBackgroundStyle.WHITE,
        navigationBarBackgroundStyle: BarBackgroundStyle.WHITE,
      },
      component: page('single-user/projects/ContactsPage'),
    },
    {
      path: '/checklist/:id',
      name: 'checklist',
      meta: {
        isStateful: true,
        statusBarBackgroundStyle: BarBackgroundStyle.WHITE,
        navigationBarBackgroundStyle: BarBackgroundStyle.WHITE,
      },
      props: (route) => ({ id: route.params.id }),
      component: page('single-user/projects/checklist/ChecklistPage'),
    },
    {
      path: '/success/:routeAfter/:phrase',
      name: 'success',
      meta: {
        showHeader: false,
        showFooter: false,
        statusBarBackgroundStyle: BarBackgroundStyle.WHITE,
        navigationBarBackgroundStyle: BarBackgroundStyle.WHITE,
      },
      props: (route) => ({
        routeAfter: route.params.routeAfter,
        phrase: route.params.phrase,
      }),
      component: page('single-user/SuccessPage'),
    },
    {
      path: '/projects/schedule/sync',
      name: 'calendarSync',
      meta: {
        showHeader: false,
        showFooter: true,
        statusBarBackgroundStyle: BarBackgroundStyle.WHITE,
        navigationBarBackgroundStyle: BarBackgroundStyle.WHITE,
      },
      component: page('single-user/projects/CalendarSyncPage'),
    },
  ]),
)

const singleUserRoutesForSettingsArea: Array<RouteRecordRaw> = deviceType(
  [DeviceType.PERSONAL],
  area(areas.SETTINGS_AREA, [
    {
      path: '/settings/clock-reminder',
      name: 'clockReminder',
      meta: {
        showGradient: false,
        showTime: false,
        scrollable: true,
        disableNativeBackButton: false,
        headerTitle: 'phrases.clock_reminder',
        hyphens: 'none',
        showBackButton: true,
        showPill: false,
        showProfilePicture: false,
        statusBarBackgroundStyle: BarBackgroundStyle.WHITE,
        navigationBarBackgroundStyle: BarBackgroundStyle.WHITE,
      },
      component: page('settings-and-legal/ClockReminderPage'),
    },
  ]),
)

const routesWithDefaultLayout = [
  {
    path: '',
    component: layout('PageLayout'),
    children: [
      ...singleUserRoutesForMyArea,
      ...singleUserRoutesForWorkArea,
      ...singleUserRoutesForSettingsArea,
    ],
  },
]

export default [...routesWithDefaultLayout, ...newOnboarding]
